/**
 * ConnectSummaryBar — the choices made so far (equipment · gateway · device ·
 * protocol · points) as small chips in the sticky step footer. Each chip goes
 * back to the step that set it.
 */

import { ChevronLeft } from "lucide-react";
import type { ConnectStepId } from "./steps";
import type { SummaryItem } from "./summary";

export function ConnectSummaryBar({
  items,
  onStep,
}: {
  items: SummaryItem[];
  onStep: (id: ConnectStepId) => void;
}) {
  if (items.length === 0) return null;
  return (
    // One line, never wrapping (the bar's height must not change as choices pile up):
    // what doesn't fit scrolls sideways, without a scrollbar, and fades out at the edge.
    <ul
      aria-label="Your choices so far"
      className="flex min-w-0 flex-nowrap items-center gap-1.5 overflow-x-auto [mask-image:linear-gradient(to_right,black_calc(100%-1.5rem),transparent)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {items.map((it) => (
        <li key={it.key} className="shrink-0">
          <button
            type="button"
            onClick={() => onStep(it.step)}
            title={`${it.label}: ${it.value}. Click to change.`}
            className="group flex max-w-44 items-center gap-1 rounded-full border border-border bg-surface-elevated px-2.5 py-1 text-xs text-on-surface transition-colors hover:border-brand/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronLeft aria-hidden className="size-3 shrink-0 text-on-surface-muted group-hover:text-brand" />
            <span className="sr-only">{it.label}: </span>
            <span className="truncate">{it.value}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}
